import PropTypes from 'prop-types';
import React, {
	useEffect, useRef, useLayoutEffect,
} from 'react';
import { ErrorBoundary } from '../Wrappers';

const elms = ({ current }) => ({
	parentElm: current?.parentElement,
	current,
	scaleElm: current?.firstElementChild,
	innerElm: current?.firstElementChild?.firstElementChild,
});

const MIN_SCALE_UPDATE = 0.002;
const MIN_PX_UPDATE = 0.5;

const setPxIfChanged = (style, fld, newVal) => {
	const oldVal = parseFloat(style[fld]);
	if (!Number.isNaN(oldVal) && Math.abs(oldVal - newVal) < MIN_PX_UPDATE) {
		return;
	}
	style[fld] = `${newVal}px`;
};

const setSize = ({ current, parentElm, scaleElm, innerElm }, minWidth) => {
	if (!minWidth || !innerElm) {
		return;
	}
	const width = parentElm.clientWidth; // Actual width (not scaled)
	const scale = (width / minWidth) || 1;

	const newWidth = width < minWidth ? minWidth : width; // "Artificial" width
	const newScale = scale < 1 ? scale : 1;

	setPxIfChanged(scaleElm.style, 'width', newWidth);
	const newHeight = innerElm.offsetHeight * newScale; // Actual height
	setPxIfChanged(current.style, 'height', newHeight);

	// Rescale
	const prevScale = Number(scaleElm.style.transform.match(/scale\(([^)]+)\)/)?.[1]) || 1;
	if (Math.abs(newScale - prevScale) >= MIN_SCALE_UPDATE) {
		Object.assign(scaleElm.style, {
			transform: `scale(${newScale})`,
			transformOrigin: '0 0',
		});
	}
};

export default function ReportRow({ minWidth, children }) {
	const ref = useRef(null);
	useLayoutEffect(() => {
		setSize(elms(ref), minWidth);
	}, [minWidth]);

	useEffect(() => {
		const { parentElm, innerElm } = elms(ref);
		const resizeObserver = new ResizeObserver(() => {
			setSize(elms(ref), minWidth);
		});

		[parentElm, innerElm].forEach((elm) => {
			if (elm) {
				resizeObserver.observe(elm);
			}
		});

		return () => {
			resizeObserver.disconnect();
		};
	}, [minWidth]);

	return (
		<ErrorBoundary>
			<div>
				<div ref={ref}>
					{children}
				</div>
			</div>
		</ErrorBoundary>
	);
}

ReportRow.propTypes = {
	minWidth: PropTypes.number,
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node,
	]).isRequired,
};

ReportRow.defaultProps = {
	minWidth: 450,
};
